import SEO from '../components/SEO';
import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2 } from 'lucide-react';

const SignpostLanding = () => {
  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <SEO 
        title="SAP Business One with Ascarii | Choose Your Perfect Path Forward"
        description="New implementation, partner transfer, or cloud migration? Explore your SAP B1 options with Ascarii. Find the right path for your business needs."
        keywords="SAP B1 options, choose SAP partner, ERP decision guide, SAP Business One paths, implementation options"
        canonical="https://www.ascarii.com/landing/signpost"
      />

      {/* Hero Question */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8 max-w-6xl relative z-10">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center">
            <div className="flex flex-wrap items-center justify-center gap-2 mb-16">
              <h1 className="text-4xl md:text-5xl lg:text-6xl leading-tight text-[#1F3DA2]">
                Are you running
              </h1>
              <img src="/signpost-images/sap-business-one-logo.jpg" alt="SAP Business One" className="h-10 md:h-12 lg:h-14 object-contain mt-2 md:mt-3" />
              <h1 className="text-4xl md:text-5xl lg:text-6xl leading-tight text-[#1F3DA2]">
                ?
              </h1>
            </div>
          </motion.div>

          {/* Two Path Cards */}
          <div className="grid md:grid-cols-2 gap-8 mt-16 max-w-7xl mx-auto">
            {/* Left: Go (New to SAP) */}
            <motion.div 
              initial={{ opacity: 0, x: -30 }} 
              animate={{ opacity: 1, x: 0 }} 
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white rounded-2xl p-8 border-2 border-green-200 hover:border-green-400 hover:shadow-xl transition-all group flex flex-col text-center"
            >
              <div className="mb-6">
                <div className="inline-block px-4 py-2 bg-green-100 border border-green-300 rounded-full text-sm font-semibold mb-4 text-green-700">
                  New to SAP Business One
                </div>
                <h2 className="text-xl md:text-2xl lg:text-3xl text-[#1F3DA2] mb-4 h-16 flex items-center justify-center">No, but I would like to know how</h2>
              </div>

              <div className="space-y-3 mb-6 text-left max-w-xs mx-auto min-h-[200px]">
                {[
                  "Zero implementation fees - no £50k+ upfront barrier",
                  "Start from £105/user/month - everything included",
                  "Unlimited business support - no pay-per-incident fees",
                  "AWS cloud hosting with 99.995% uptime included",
                  "Scale from 1 to 100+ users as you grow"
                ].map((benefit, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>

              <p className="text-gray-700 text-sm mb-6 leading-relaxed">
                <strong>Ascarii Go:</strong> SAP Business One as pure subscription. Everything included - no massive upfront costs.
              </p>

              <a href="/landing/ascarii-go" className="block mt-auto">
                <button className="w-full bg-gradient-to-r from-green-500 to-emerald-500 text-white px-8 py-4 rounded-xl text-xl font-bold hover:from-green-600 hover:to-emerald-600 transition-all shadow-lg">
                  Learn More
                </button>
              </a>
            </motion.div>

            {/* Right: Run (Existing SAP) */}
            <motion.div 
              initial={{ opacity: 0, x: 30 }} 
              animate={{ opacity: 1, x: 0 }} 
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white rounded-2xl p-8 border-2 border-blue-200 hover:border-blue-400 hover:shadow-xl transition-all group flex flex-col text-center"
            >
              <div className="mb-6">
                <div className="inline-block px-4 py-2 bg-blue-100 border border-blue-300 rounded-full text-sm font-semibold mb-4 text-blue-700">
                  Existing SAP Business One Customer
                </div>
                <h2 className="text-xl md:text-2xl lg:text-3xl text-[#1F3DA2] mb-4 h-16 flex items-center justify-center">Yes, but I would like to run better</h2>
              </div>

              <div className="space-y-3 mb-6 text-left max-w-xs mx-auto min-h-[200px]">
                {[
                  "Free upgrades to latest SAP B1 - zero professional services fees",
                  "Free 30-day test environment for every upgrade",
                  "Free AWS cloud migration - no infrastructure project costs",
                  "Service Hours from £123/hour that never expire",
                  "Dedicated account manager - genuine partnership support"
                ].map((benefit, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>

              <p className="text-gray-700 text-sm mb-6 leading-relaxed">
                <strong>Partner Transfer:</strong> Free upgrades, free test environments, and partnership support. Get more from your SAP investment.
              </p>

              <a href="/landing/partner-transfer" className="block mt-auto">
                <button className="w-full bg-gradient-to-r from-blue-500 to-sky-500 text-white px-8 py-4 rounded-xl text-xl font-bold hover:from-blue-600 hover:to-sky-600 transition-all shadow-lg">
                  Learn More
                </button>
              </a>
            </motion.div>
          </div>

          {/* Trust Badges - Between Cards and CTA */}
          <div className="flex flex-wrap justify-center gap-8 mt-12 mb-12">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-blue-600" />
              <span className="text-gray-700 font-medium">SAP Business One Premier Partner</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-blue-600" />
              <span className="text-gray-700 font-medium">AWS Advanced Partner</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-blue-600" />
              <span className="text-gray-700 font-medium">15+ Years Experience</span>
            </div>
          </div>

          {/* Talk to Specialist CTA */}
          <div className="mt-16">
            <div className="bg-gradient-to-r from-blue-600 to-sky-600 rounded-2xl p-12 text-white text-center shadow-2xl">
              <h3 className="text-3xl font-bold mb-4">Not Sure Which Path is Right for You?</h3>
              <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
                Speak with one of our SAP Business One specialists. We'll discuss your situation and help you understand which option best fits your needs - no pressure, just clarity.
              </p>
              <a href="/contact">
                <button className="bg-white text-blue-600 hover:bg-blue-50 px-10 py-4 rounded-xl text-lg font-semibold transition-all shadow-lg">
                  Talk to a Specialist
                </button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SignpostLanding;
